import { isWebbedExtraInfoEnabled } from "../../../helpers/webbedExtraInfo";

export const WEBBED_GUEST_NAME_MIN_LENGTH = 2;
export const WEBBED_GUEST_NAME_MAX_LENGTH = 25;

const LETTERS_ONLY_RE = /^[A-Za-z]+$/;

export function shouldEnforceWebbedGuestNameRules(appConfig: any, selectedFlightOptions: any): boolean {
  return !!selectedFlightOptions?.hotels && isWebbedExtraInfoEnabled(appConfig);
}

export function isValidWebbedGuestNameInput(value: string): boolean {
  if (value === "") return true;
  return LETTERS_ONLY_RE.test(value);
}

export function isValidWebbedGuestName(value: string): boolean {
  const v = (value ?? "").trim();
  return v.length >= WEBBED_GUEST_NAME_MIN_LENGTH && v.length <= WEBBED_GUEST_NAME_MAX_LENGTH && LETTERS_ONLY_RE.test(v);
}

export function isValidWebbedOptionalGuestName(value: string): boolean {
  const v = (value ?? "").trim();
  if (v === "") return true;
  return v.length <= WEBBED_GUEST_NAME_MAX_LENGTH && LETTERS_ONLY_RE.test(v);
}

const FIELD_LABEL: { [key: string]: string } = { first: "First", middle: "Middle", last: "Last" };

export function getWebbedGuestNameError(value: string, field: "first" | "middle" | "last"): string {
  const v = (value ?? "").trim();
  const label = FIELD_LABEL[field];
  if (field === "middle" && v === "") return "";
  if (v === "" || v.length < WEBBED_GUEST_NAME_MIN_LENGTH) {
    return `Enter a valid ${label.toLowerCase()} name (minimum ${WEBBED_GUEST_NAME_MIN_LENGTH} characters).`;
  }
  if (v.length > WEBBED_GUEST_NAME_MAX_LENGTH) {
    return `${label} name too long (max ${WEBBED_GUEST_NAME_MAX_LENGTH} characters).`;
  }
  if (!LETTERS_ONLY_RE.test(v)) {
    return `Enter a valid ${label.toLowerCase()} name (letters only — no spaces or special characters).`;
  }
  return "";
}

const normalizedFullName = (traveler: any): string =>
  [traveler?.firstName, traveler?.middleName, traveler?.lastName]
    .map((part) => (part ?? "").trim().toLowerCase())
    .filter(Boolean)
    .join(" ");

const guestKey = (traveler: any, index: number): string => traveler?.id ?? traveler?.extraId ?? `#${index}`;

export function findDuplicateWebbedGuestKeys(travelers: any[] | null | undefined): Set<string> {
  const keysByName: { [name: string]: string[] } = {};
  (travelers || []).forEach((traveler: any, index: number) => {
    if (!traveler?.firstName?.trim() || !traveler?.lastName?.trim()) return;
    const name = normalizedFullName(traveler);
    if (!name) return;
    (keysByName[name] ||= []).push(guestKey(traveler, index));
  });
  const duplicates = new Set<string>();
  Object.values(keysByName).forEach((keys) => {
    if (keys.length > 1) keys.forEach((key) => duplicates.add(key));
  });
  return duplicates;
}

export function isDuplicateWebbedGuestName(travelers: any[] | null | undefined, index: number): boolean {
  const traveler = travelers?.[index];
  if (!traveler?.firstName?.trim() || !traveler?.lastName?.trim()) return false;
  return findDuplicateWebbedGuestKeys(travelers).has(guestKey(traveler, index));
}

export const DUPLICATE_GUEST_NAME_ERROR = "This name matches another guest. Passenger names must be unique.";

export function getWebbedGuestNameListError(travelers: any[] | null | undefined): string | null {
  const list = travelers || [];
  for (const traveler of list) {
    const firstErr = getWebbedGuestNameError(traveler?.firstName, "first");
    if (firstErr) return firstErr;
    const lastErr = getWebbedGuestNameError(traveler?.lastName, "last");
    if (lastErr) return lastErr;
    const middleErr = getWebbedGuestNameError(traveler?.middleName, "middle");
    if (middleErr) return middleErr;
  }
  if (findDuplicateWebbedGuestKeys(list).size > 0) return DUPLICATE_GUEST_NAME_ERROR;
  return null;
}
